import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Row as VanRow,
  Col as VanCol,
  showImagePreview,
  showToast,
} from 'vant'
import { DrawBallsBox } from '@/components/lottery-ball-row'
import type { DrawBalls } from '@/components/lottery-ball-row'
import {
  renderTukuVoteGrid,
  renderZodiacVoteDialog,
  toTukuVoteOptions,
} from '@/components/zodiac-vote'
import { renderGalleryStickyBar } from '@/components/gallery-period-selectors'
import { GalleryExternalLinks } from '@/components/external-links'
import type { BannerItem } from '@/components/external-links'
import {
  CommentSection,
  SocialStatsRow,
  SOCIAL_SCENE,
  useSocialSync,
} from '@/components/social-section'
import { useGalleryPeriod, fetchTukuDrawBalls } from '@/composables/use-gallery-period'
import { useRegionStore } from '@/stores/region-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { useAuthStore } from '@/stores/auth-store'
import {
  getApiV1BannersList,
  getApiV1TukuImagesDetailByPeriod,
  getApiV1TukuSimilar,
  getApiV1TukuVoteSummary,
  getApiV1UserLike,
  postApiV1ContentCollect,
  postApiV1TukuVote,
} from '@/api'
import type {
  Banner,
  TukuFace,
  TukuImageDetail,
  TukuSimilarItem,
  TukuVoteSummary,
} from '@/api'

function mapBanner(b: Banner): BannerItem {
  return {
    id: String(b.id ?? ''),
    imageUrl: b.images ?? '',
    linkUrl: b.url || undefined,
  }
}

export default defineComponent({
  name: 'GalleryDetailPage',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const goBack = useSmartBack()
    const regionStore = useRegionStore()
    const authStore = useAuthStore()

    const qRegion = route.query.region
    if (qRegion === 'hk' || qRegion === 'aocai') {
      regionStore.setRegion(qRegion)
    }

    const pictureTypeId = computed(() => String(route.params.id))

    const albumName = ref('')

    // SEO：图库详情页，标题随图纸名称动态变化（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `${albumName.value || '六合图库图纸'}_高清图纸资料 - ${brand.value}`,
      description: () =>
        `${albumName.value || '六合图库'}高清图纸资料，含开奖号码参考与历史资料，图片清晰、更新及时。`,
      keywords: () => `${albumName.value || '六合图库'},图纸,高清资料图片,六合图库,${brand.value}`,
      ogType: 'article',
    })

    const detail = ref<TukuImageDetail | null>(null)
    const lotteryResult = ref<DrawBalls | null>(null)
    const banners = ref<BannerItem[]>([])
    const remList = ref<TukuSimilarItem[]>([])
    // 切换期数后推荐模块消失，不再重新请求
    const remVisible = ref(true)

    // 投票相关
    const vitoShow = ref(false)
    const dialogShow = ref(false)
    const animalId = ref<number | null>(null)
    const voteSummary = ref<TukuVoteSummary | null>(null)
    const voteSubmitting = ref(false)

    // sky 快速评论条显隐（对齐 49208：scrollY > 370 才显示）
    const skyShow = ref(false)
    const onScroll = () => { skyShow.value = window.scrollY > 370 }
    onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
    onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))

    const socialSync = useSocialSync()
    watch(detail, (v) => {
      socialSync.liked.value = !!v?.thumbUp
      socialSync.collected.value = !!v?.collected
      socialSync.likeCount.value = v?.thumbUpCount ?? v?.current?.like_count ?? 0
      socialSync.collectCount.value = v?.collectCount ?? 0
    })

    async function fetchImage(year: number, period: number) {
      const res = await getApiV1TukuImagesDetailByPeriod({
        query: { region: regionStore.region, pictureTypeId: pictureTypeId.value, year, period },
      })
      const d = res.data?.data ?? null
      detail.value = d
      if (!albumName.value && d?.current?.tuku?.name) albumName.value = d.current.tuku.name
      await fetchVoteSummary(year, period)
    }

    async function fetchLotteryResult(year: number, period: number) {
      lotteryResult.value = await fetchTukuDrawBalls(regionStore.region, year, period)
    }

    async function fetchSimilar() {
      const tukuId = detail.value?.current?.tuku?.id
      if (!tukuId) return
      const res = await getApiV1TukuSimilar({
        query: { gallery_id: Number(tukuId) },
      })
      remList.value = res.data?.data?.result_list ?? []
    }

    async function fetchVoteSummary(year: number, period: number) {
      const tukuId = detail.value?.current?.tuku?.id
      if (!tukuId) {
        voteSummary.value = null
        return
      }
      const res = await getApiV1TukuVoteSummary({
        query: { tuku_id: Number(tukuId), year, period },
      })
      voteSummary.value = res.data?.data ?? null
      animalId.value = voteSummary.value?.my_animal_id || null
    }

    const galleryPeriod = useGalleryPeriod(() => pictureTypeId.value, async (year, period) => {
      await Promise.all([fetchImage(year, period), fetchLotteryResult(year, period)])
    })
    const { availableYears, selectedYear, selectedPeriod, yearList, periodOptionList, fetchYears, handleYearChange, initFromRoute } = galleryPeriod

    async function handlePeriodChange(periodId: number | string) {
      remVisible.value = false
      await galleryPeriod.handlePeriodChange(periodId)
    }

    async function init() {
      albumName.value = ''
      availableYears.value = []
      detail.value = null
      lotteryResult.value = null
      remList.value = []
      remVisible.value = true
      vitoShow.value = false
      voteSummary.value = null
      animalId.value = null

      if (route.query.region) {
        const lotteryTypeCode = {
          hk: 'hk',
          aocai: 'macau',
          macau: 'macau',
        } as const
        const region = route.query.region
        const lotteryType = typeof region === 'string' ? lotteryTypeCode[region as keyof typeof lotteryTypeCode] : undefined
        if (lotteryType) {
          regionStore.setLotteryType(lotteryType === 'hk' ? 'hk' : 'macau')
        }
      }

      const [, bannerList] = await Promise.all([
        fetchYears(),
        getApiV1BannersList({ query: { page: 1, size: 999, status: 1, type: 2 } }),
      ])
      banners.value = (bannerList.data?.data?.list ?? []).map(mapBanner)

      await initFromRoute()
      await fetchSimilar()
    }

    onMounted(init)

    watch(() => route.params.id, (newId, oldId) => {
      if (newId !== oldId) init()
    })

    watch(
      () => regionStore.region,
      async () => {
        albumName.value = ''
        detail.value = null
        lotteryResult.value = null
        await fetchYears()
        const first = availableYears.value[0]
        if (first) await handleYearChange(first)
      },
    )

    const currentImage = computed(() => detail.value?.current?.image ?? null)
    const currentTargetId = computed(() => detail.value?.current?.id ?? null)
    const commentSectionRef = ref<{ openCommentPopup: () => void } | null>(null)

    // summary 加载前用 12 生肖兜底
    const currentVoteList = computed(() => toTukuVoteOptions(voteSummary.value))

    async function handleDialogConfirm() {
      if (!animalId.value) {
        showToast('请选择投票选项')
        return
      }
      if (voteSummary.value?.is_drawn) {
        showToast('本期已开奖，无法投票')
        return
      }
      const tukuId = detail.value?.current?.tuku?.id
      const year = selectedYear.value
      const period = selectedPeriod.value
      if (!tukuId || !year || !period) return
      if (voteSubmitting.value) return
      voteSubmitting.value = true
      try {
        const res = await postApiV1TukuVote({
          body: {
            tuku_id: Number(tukuId),
            year,
            period,
            animal_id: animalId.value,
          },
        })
        // HTTP 非 2xx（如 code=400 重复投票）时业务体在 res.error 中，需一并读取后端 message
        const body = (res.data ?? res.error) as typeof res.data
        if (body?.code !== 0) {
          showToast(body?.message || '投票失败')
          return
        }
        voteSummary.value = body.data ?? null
        animalId.value = voteSummary.value?.my_animal_id || null
        vitoShow.value = true
        showToast('投票成功')
      } finally {
        voteSubmitting.value = false
      }
    }

    // tjxzBotom 点赞/收藏（TUKU 场景：like_type=1, collect target_type=5）
    const likeLoading = ref(false)
    async function handleLike() {
      if (!authStore.isLoggedIn) { router.push('/login'); return }
      if (!currentTargetId.value || likeLoading.value) return
      likeLoading.value = true
      const next = !socialSync.liked.value
      socialSync.liked.value = next
      socialSync.likeCount.value += next ? 1 : -1
      try {
        await getApiV1UserLike({ query: { id: currentTargetId.value, type: 1 } })
      } catch {
        socialSync.liked.value = !next
        socialSync.likeCount.value += next ? -1 : 1
      } finally {
        likeLoading.value = false
      }
    }
    async function handleCollect() {
      if (!authStore.isLoggedIn) { router.push('/login'); return }
      const cid = detail.value?.collectTargetId ?? currentTargetId.value
      if (!cid) return
      const next = !socialSync.collected.value
      socialSync.collected.value = next
      socialSync.collectCount.value += next ? 1 : -1
      try {
        await postApiV1ContentCollect({ body: { target_type: 5, target_id: cid, favorite: next ? 1 : 0 } })
      } catch {
        socialSync.collected.value = !next
        socialSync.collectCount.value += next ? -1 : 1
      }
    }

    function previewImage() {
      if (currentImage.value) {
        showImagePreview({ images: [currentImage.value], closeable: true })
      }
    }


    const faceList = computed<TukuFace[]>(() => detail.value?.current?.faces ?? [])

    function handleFaceChange(face: TukuFace) {
      if (face.pictureTypeId == null || face.isCurrent) return
      router.replace(
        `/gallery/${face.pictureTypeId}?region=${regionStore.region}` +
          `&year=${selectedYear.value}&period=${selectedPeriod.value ?? ''}`,
      )
    }

    // 「查看图解」：当前期没有图库 id 或期号时点了只提示
    const illustrationTo = computed(() => {
      const cur = detail.value?.current
      const tukuId = cur?.tuku?.id
      const period = cur?.period
      if (!tukuId || !period) return null
      return {
        path: `/gallery/${tukuId}/illustrations`,
        query: {
          period: String(period),
          year: String(cur?.year ?? selectedYear.value),
          tukuName: cur?.tuku?.name ?? albumName.value,
          lotteryType: regionStore.region === 'hk' ? '1' : '2',
        },
      }
    })

    function publishComments() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      if (currentTargetId.value == null) return
      commentSectionRef.value?.openCommentPopup()
    }


    return () => (
      <div class="picture_details">
        <VanNavBar
          fixed
          clickable={false}
          title={albumName.value || '图库详情'}
          onClickLeft={() => goBack()}
        >
          {{
            left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
            right: () => (
              <>
                <RouterLink class="rht rht1 btn-reset" to="/history">
                  <i class="icons" />
                  <span class="descs">开奖记录</span>
                </RouterLink>
                <button type="button" class="rht rht2 btn-reset" onClick={publishComments}>
                  <i class="icons" />
                  <span class="descs">发表评论</span>
                </button>
              </>
            ),
          }}
        </VanNavBar>

        <div class={['m-scene-vote', faceList.value.length > 1 ? '!pt-[3.4rem]' : '']}>
          {/* 顶部固定条：年份 + 期数选择器（fixed 在 nav-bar 下方） */}
          {renderGalleryStickyBar({
            yearList: yearList.value,
            year: selectedYear.value,
            onYearChange: handleYearChange,
            periodList: periodOptionList.value,
            period: selectedPeriod.value,
            onPeriodChange: handlePeriodChange,
            faces: faceList.value,
            onFaceChange: handleFaceChange,
          })}

        {/* 彩球行（.border-box.testt） */}
        {lotteryResult.value && (
          <DrawBallsBox draw={lotteryResult.value}>
            {selectedYear.value}年 第{String(selectedPeriod.value ?? '').padStart(3, '0')}期
          </DrawBallsBox>
        )}

        {/* 主图片 */}
        {currentImage.value && (
          <button type="button" class="ms-bd-img btn-block" onClick={previewImage}>
            <img
              src={currentImage.value}
              alt={albumName.value || '图库详情'}
              referrerpolicy="no-referrer"
            />
          </button>
        )}

        {/* 投票展示区 ms-bd-vote（展开时显示，各生肖票数+进度条） */}
        {vitoShow.value && renderTukuVoteGrid(currentVoteList.value)}

        {/* 投票/投票记录按钮（展开时显示） */}
        {vitoShow.value && (
          <div class="ms-bd-btn">
            <VanRow gutter={30}>
              <VanCol span={12}>
                <button
                  class="van-button btn-bg"
                  onClick={() => {
                    if (!authStore.isLoggedIn) { router.push('/login'); return }
                    dialogShow.value = true
                  }}
                >
                  投票
                </button>
              </VanCol>
              <VanCol span={12}>
                <button
                  class="van-button btn-bd"
                  onClick={() =>
                    router.push({
                      path: `/gallery/${route.params.id}/vote-records`,
                      query: {
                        region: regionStore.region,
                        year: String(selectedYear.value),
                        period: String(selectedPeriod.value ?? ''),
                      },
                    })
                  }
                >
                  投票记录
                </button>
              </VanCol>
            </VanRow>
          </div>
        )}

        {/* 展开/收起投票 */}
        <div class="open-hide">
          {/* 样式挂在 `.open-hide span` 上：span 保留，按钮包在外面 */}
          <button type="button" class="btn-reset" onClick={() => (vitoShow.value = !vitoShow.value)}>
            <span class={{ open: vitoShow.value }}>
              <i />
              <label>{vitoShow.value ? '收起投票' : '展开投票'}</label>
            </span>
          </button>
        </div>
        </div>

        {/* 投票弹窗 */}
        {renderZodiacVoteDialog({
          show: dialogShow,
          selected: animalId,
          list: currentVoteList.value,
          onConfirm: handleDialogConfirm,
        })}

        {/* 点赞评论栏 */}
        {currentTargetId.value != null && (
          <div class="v-interaction">
            <SocialStatsRow
              scene={SOCIAL_SCENE.TUKU}
              targetId={currentTargetId.value}
              collectTargetId={detail.value?.collectTargetId}
              likeCount={detail.value?.thumbUpCount ?? detail.value?.current?.like_count ?? 0}
              commentCount={detail.value?.comments_count ?? 0}
              collectCount={detail.value?.collectCount ?? 0}
              readCount={detail.value?.clickCount ?? 0}
              initialLiked={!!detail.value?.thumbUp}
              initialCollected={!!detail.value?.collected}
            />
          </div>
        )}

        {/* 推荐图纸（仅首次进入展示，切换期数后隐藏） */}
        {remVisible.value && remList.value.length > 0 && (
          <div class="body-image-hds">
            <div class="mtitle">为您推荐类似图纸:</div>
            <div class="set1">
              <div class="set2">
                {remList.value.map((item) => (
                  // 不带 year/period，由目标页 init 取该图库自身的最新年份/期数（avoid latest_issue 与图库 periods 期号口径不一致）
                  <RouterLink
                    key={item.id}
                    class="set2-warp btn-reset"
                    to={`/gallery/${item.picture_type_id}?region=${item.lottery_type === 1 ? 'hk' : 'aocai'}`}
                    onClick={() => regionStore.setLotteryType(item.lottery_type === 1 ? 'hk' : 'macau')}
                  >
                    <div class="icon-bgs">
                      {item.latest_image && <img src={item.latest_image} alt="" referrerpolicy="no-referrer" />}
                    </div>
                    <div class="text">{item.name}</div>
                  </RouterLink>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 网址大全 */}
        <GalleryExternalLinks banners={banners.value} />

        {/* 评论区（不用内置 tool-bottom，用本页的 sky + tjxzBotom 替代） */}
        {currentTargetId.value != null && (
          <CommentSection
            ref={commentSectionRef}
            scene={SOCIAL_SCENE.TUKU}
            targetId={currentTargetId.value}
            lotteryType={regionStore.region === 'aocai' ? 2 : 1}
            collectTargetId={detail.value?.collectTargetId}
            likeCount={detail.value?.thumbUpCount ?? 0}
            commentCount={detail.value?.comments_count ?? 0}
            collectCount={detail.value?.collectCount ?? 0}
            initialLiked={!!detail.value?.thumbUp}
            initialCollected={!!detail.value?.collected}
            hideToolBottom
          />
        )}

        {/* sky 快速评论条（固定在 tjxzBotom 上方，仅当 scrollY > 370 显示） */}
        {/* 原来 .sky 一直在、内容按滚动显示；按钮只在显示时渲染，免得留一个看不见的 Tab 落点 */}
        {skyShow.value && (
          <button type="button" class="sky btn-reset block" onClick={publishComments}>
            <VanRow class="input-btn">
              <VanCol class="cont" span={21}>
                <span class="left">来发布您的评论吧</span>
              </VanCol>
              <VanCol class="num" span={3}>
                <VanIcon name="chat-o" />
                {detail.value?.comments_count ?? 0}
              </VanCol>
            </VanRow>
          </button>
        )}

        {/* tjxzBotom 底部工具栏 */}
        <div class="tjxzBotom">
          <button type="button" class="item tz btn-reset" onClick={publishComments}>
            <span class="icons" />
            <span class="descs">评论</span>
          </button>
          {illustrationTo.value ? (
            <RouterLink class="item ckpl btn-reset" to={illustrationTo.value}>
              <span class="icons" />
              <span class="descs">查看图解</span>
            </RouterLink>
          ) : (
            <button
              type="button"
              class="item ckpl btn-reset"
              onClick={() => showToast('当前图库未配置图解')}
            >
              <span class="icons" />
              <span class="descs">查看图解</span>
            </button>
          )}
          <button
            type="button"
            class={['item sc btn-reset', { selected: socialSync.collected.value }]}
            onClick={handleCollect}
          >
            <span class="icons" />
            <span class="descs">{socialSync.collected.value ? '已收藏' : '收藏'}</span>
          </button>
          <button
            type="button"
            class={['item dz btn-reset', { selected: socialSync.liked.value }]}
            onClick={handleLike}
          >
            <span class="icons" />
            <span class="descs">点赞</span>
          </button>
          <button type="button" class="item zf btn-reset" onClick={() => {
            if (navigator.share) navigator.share({ title: document.title, url: location.href }).catch(() => {})
            else { navigator.clipboard.writeText(location.href).catch(() => {}); showToast('链接已复制') }
          }}>
            <span class="icons" />
            <span class="descs">转发</span>
          </button>
        </div>
      </div>
    )
  },
})
